import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ThreeDotIcon from "../../SVG/three-dot-icon";
import "./tag-with-actions.scss";

export interface TagActionOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: (option: TagActionOption) => void;
  disabled?: boolean;
}

export interface TagWithActionsProps {
  label: string;
  tagId?: string;
  backgroundColor?: string;
  textColor?: string;
  options?: TagActionOption[];
  onMenuOpenChange?: (open: boolean) => void;
  className?: string;
  getPortalContainer?: () => HTMLElement;
  showActions?: boolean;
  onTagClick?: (tagId?: string) => void;
}

const MENU_OFFSET = 6;
const MENU_APPROX_WIDTH = 140;
const MENU_APPROX_HEIGHT = 120;

export function TagWithActions({
  label,
  tagId,
  backgroundColor,
  textColor,
  options = [],
  onMenuOpenChange,
  className = "",
  getPortalContainer,
  showActions = true,
  onTagClick,
}: TagWithActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    bottom: boolean;
    leftAlign: boolean;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.right;
    const bottom = spaceBelow >= MENU_APPROX_HEIGHT || rect.top < spaceBelow;
    const leftAlign = spaceRight >= MENU_APPROX_WIDTH;
    setPosition({
      top: bottom ? rect.bottom + MENU_OFFSET : rect.top - MENU_OFFSET,
      left: leftAlign ? rect.left : rect.right,
      bottom,
      leftAlign,
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      updatePosition();
    } else {
      setPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        wrapperRef.current &&
        menuRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onMenuOpenChange?.(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onMenuOpenChange]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isOpen;
    setIsOpen(next);
    onMenuOpenChange?.(next);
  };

  const handleOptionClick = (option: TagActionOption) => {
    if (option.disabled) return;
    option.onClick(option);
    setIsOpen(false);
    onMenuOpenChange?.(false);
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent,
    option: TagActionOption
  ) => {
    if (option.disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOptionClick(option);
    }
  };

  const portalTarget = getPortalContainer?.() ?? document.body;

  const dropdownContent =
    isOpen && position ? (
      <div
        ref={menuRef}
        role="menu"
        className="tag-with-actions__menu"
        style={{
          position: "fixed",
          top: position.bottom ? position.top : undefined,
          bottom: position.bottom
            ? undefined
            : `calc(100vh - ${position.top}px)`,
          left: position.leftAlign ? position.left : undefined,
          right: position.leftAlign
            ? undefined
            : `calc(100vw - ${position.left}px)`,
        }}
      >
        {options.map((option) => (
          <div
            key={option.id}
            role="menuitem"
            aria-disabled={option.disabled}
            className={`tag-with-actions__menu-item ${option.disabled ? "tag-with-actions__menu-item--disabled" : ""}`}
            tabIndex={option.disabled ? -1 : 0}
            onClick={() => handleOptionClick(option)}
            onKeyDown={(e) => handleOptionKeyDown(e, option)}
          >
            {option.icon != null && (
              <span className="tag-with-actions__menu-icon" aria-hidden>
                {option.icon}
              </span>
            )}
            <span className="tag-with-actions__menu-label">{option.label}</span>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div
      className={`tag-with-actions ${className}`.trim()}
      ref={wrapperRef}
    >
      <span
        className={`tag-with-actions__badge${onTagClick ? " tag-with-actions__badge--clickable" : ""}`}
        style={{
          backgroundColor,
          color: textColor,
        }}
        role={onTagClick ? "button" : undefined}
        tabIndex={onTagClick ? 0 : undefined}
        onClick={onTagClick ? () => onTagClick(tagId) : undefined}
        onKeyDown={
          onTagClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onTagClick(tagId);
                }
              }
            : undefined
        }
      >
        {label}
      </span>

      {showActions && (
        <>
          <button
            ref={triggerRef}
            type="button"
            className={`tag-with-actions__trigger ${isOpen ? "tag-with-actions__trigger--active" : ""}`}
            onClick={handleTriggerClick}
            aria-label="Open actions menu"
            aria-haspopup="menu"
            aria-expanded={isOpen}
          >
            <ThreeDotIcon />
          </button>

          {dropdownContent != null && createPortal(dropdownContent, portalTarget)}
        </>
      )}
    </div>
  );
}
